/**
 * Searchable single-select, drawn as finixt-ui's Combobox: Radix Popover
 * for the panel, the outside click, Escape, and the return of focus to the
 * trigger; cmdk for the filter, the arrow keys, and Enter. Use it where a
 * Select would exceed a dozen options, and always for anything drawn from
 * real records.
 *
 * Two things finixt-ui's does not do: `clearable` draws a close glyph that
 * empties the value, and `onCreate` offers the typed query as a row, for a
 * field whose list exists to make a string right, never to refuse one.
 *
 * `searchable={false}` drops the search row; ui/Select is this panel without
 * it for a short closed list, so every list in the console opens the same way.
 *
 * An option may name a group, drawn as cmdk's group heading so a search hides
 * a group with nothing left; carry a grey detail after its label and a mono
 * aside at the right; and give the closed field its own text, cut from the
 * start so its end stays in view, as a field's path does (Feature 73 D2). A
 * short closed list may say what each option does on a plain second line.
 */
import * as Popover from '@radix-ui/react-popover';
import { Command } from 'cmdk';
import { Fragment, useRef, useState, type ReactNode } from 'react';

import { Icon, IconButton } from './finixt.js';

const PANEL_Z_INDEX = 86;
/** Narrowest panel with a search row: room for the field and its placeholder. */
const PANEL_MIN_WIDTH_SEARCH = 232;
/** Narrowest panel without one: a short value list such as rows per page. */
const PANEL_MIN_WIDTH_PLAIN = 120;

/** Plain substring over the label and keywords, as finixt-ui's; cmdk's fuzzy default keeps too much. */
function contains(value: string, search: string, keywords?: string[]): number {
  const haystack = `${value} ${(keywords ?? []).join(' ')}`.toLowerCase();
  return haystack.includes(search.trim().toLowerCase()) ? 1 : 0;
}

export interface ComboboxOption {
  value: string;
  label: string;
  /** Mono second line: a code, an owner, a count. */
  meta?: string | undefined;
  /** Plain second line: what the option does, in a few words. */
  description?: string | undefined;
  disabled?: boolean | undefined;
  /** The heading this option sits under; consecutive options share one. */
  group?: string | undefined;
  /** Grey text after the label, on its line: where the option sits. */
  detail?: string | undefined;
  /** Mono text at the right of the row and of the closed field: a type, a unit. */
  aside?: string | undefined;
  /** `warn` draws the aside in the warning colour, for an option that should change. */
  asideTone?: 'warn' | undefined;
  /** The closed field's text when this option is chosen, cut from the start. */
  selectedText?: string | undefined;
  /** The closed field's hover text when this option is chosen. */
  title?: string | undefined;
}

/** Consecutive options under the same heading, in order. */
function grouped(
  options: readonly ComboboxOption[],
): { group?: string; options: ComboboxOption[] }[] {
  const runs: { group?: string; options: ComboboxOption[] }[] = [];
  for (const option of options) {
    const last = runs.at(-1);
    if (last && last.group === option.group) last.options.push(option);
    else runs.push({ ...(option.group ? { group: option.group } : {}), options: [option] });
  }
  return runs;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string | undefined;
  searchPlaceholder?: string | undefined;
  /** Draws a close glyph that empties the value. */
  clearable?: boolean | undefined;
  disabled?: boolean | undefined;
  /** Offers the typed query as a row; receives it when chosen. */
  onCreate?: ((query: string) => void) | undefined;
  /** The create row's text for a query. Default: Create “query”. */
  createLabel?: ((query: string) => ReactNode) | undefined;
  /** Replaces the default "Nothing matches “query”." */
  emptyLabel?: ReactNode;
  'aria-label'?: string | undefined;
  size?: 'sm' | 'md' | undefined;
  /** The trigger's id, so a Field label names it. */
  id?: string | undefined;
  /** Draws the search row; on by default. */
  searchable?: boolean | undefined;
  /** The trigger's width; the default fills the container. */
  width?: number | string | undefined;
  /** The panel's least width, when it should open wider than the field. */
  panelWidth?: number | undefined;
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Search…',
  searchPlaceholder,
  clearable = false,
  disabled = false,
  onCreate,
  createLabel,
  emptyLabel,
  'aria-label': ariaLabel,
  size = 'md',
  id,
  searchable = true,
  width = '100%',
  panelWidth,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const commandRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const selected = options.find((option) => option.value === value);
  const trimmed = query.trim();
  const offerCreate =
    onCreate !== undefined &&
    trimmed !== '' &&
    !options.some((option) => option.value === trimmed || option.label === trimmed);

  function close() {
    setOpen(false);
    setQuery('');
  }

  function pick(next: string) {
    onChange(next);
    close();
  }

  return (
    <Popover.Root open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
      <Popover.Anchor asChild>
        <div style={{ position: 'relative', width }}>
          <Popover.Trigger asChild>
            <button
              type="button"
              id={id}
              disabled={disabled}
              aria-label={ariaLabel}
              aria-haspopup="listbox"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                width: '100%',
                boxSizing: 'border-box',
                padding: size === 'sm' ? '5px 9px' : 'var(--pad-input)',
                // Only a present key: React writes an undefined one as an empty
                // right padding, which drops the shorthand's right side.
                ...(clearable && value ? { paddingRight: 58 } : {}),
                border: `1px solid ${open ? 'var(--accent)' : 'var(--border-input)'}`,
                borderRadius: 'var(--radius-input)',
                background: 'var(--surface-card)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                fontFamily: 'var(--font-sans)',
                textAlign: 'left',
                transition: 'border-color var(--dur-micro) var(--ease-standard)',
              }}
            >
              <span
                title={selected?.title}
                // Text that wants its whole width: a container may widen to show it.
                data-fit-text=""
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: size === 'sm' ? 'var(--text-body-sm)' : 'var(--text-body-md)',
                  color: selected ? 'var(--text-strong)' : 'var(--text-ghost)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  // A right-to-left box clips the start of its left-to-right text.
                  ...(selected?.selectedText ? { direction: 'rtl', textAlign: 'left' } : {}),
                }}
              >
                {selected?.selectedText ? (
                  <bdi>{selected.selectedText}</bdi>
                ) : selected ? (
                  selected.label
                ) : (
                  value || placeholder
                )}
              </span>
              {selected?.aside ? (
                <span
                  className="mono"
                  style={{
                    flexShrink: 0,
                    fontSize: 'var(--text-label-lg)',
                    color: selected.asideTone === 'warn' ? 'var(--warn)' : 'var(--text-soft)',
                  }}
                >
                  {selected.aside}
                </span>
              ) : null}
              <Icon name="unfold_more" size={17} color="var(--text-faint)" />
            </button>
          </Popover.Trigger>
          {clearable && value && !disabled ? (
            <IconButton
              icon="close"
              size={22}
              tone="bare"
              aria-label="Clear selection"
              onClick={() => onChange('')}
              style={{ position: 'absolute', right: 30, top: '50%', transform: 'translateY(-50%)' }}
            />
          ) : null}
        </div>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={5}
          onOpenAutoFocus={(event) => {
            // The search field focuses itself; without one, focus goes inside the
            // command root so the arrow keys and Enter reach the list.
            event.preventDefault();
            if (!searchable) commandRef.current?.focus();
          }}
          style={{
            zIndex: PANEL_Z_INDEX,
            width: 'var(--radix-popover-trigger-width)',
            minWidth: Math.max(
              panelWidth ?? 0,
              searchable ? PANEL_MIN_WIDTH_SEARCH : PANEL_MIN_WIDTH_PLAIN,
            ),
            background: 'var(--surface-card)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-panel)',
            boxShadow: 'var(--shadow-popover)',
            animation: 'popin var(--dur-fast) var(--ease-out)',
            outline: 'none',
          }}
        >
          <Command
            ref={commandRef}
            tabIndex={-1}
            label={ariaLabel ?? placeholder}
            loop
            filter={contains}
            // Open on the chosen row, as a select does.
            {...(selected ? { defaultValue: selected.value } : {})}
            style={{ outline: 'none' }}
          >
            {searchable ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  padding: '9px 11px',
                  borderBottom: '1px solid var(--border-divider)',
                }}
              >
                <Icon name="search" size={16} color="var(--text-faint)" />
                <Command.Input
                  autoFocus
                  value={query}
                  onValueChange={setQuery}
                  placeholder={searchPlaceholder ?? placeholder}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-body-sm)',
                    color: 'var(--text-strong)',
                  }}
                />
              </div>
            ) : null}
            <Command.List style={{ maxHeight: 240, overflowY: 'auto', padding: 5 }}>
              <Command.Empty
                style={{
                  padding: '13px 11px',
                  fontSize: 'var(--text-body-sm)',
                  color: 'var(--text-faint)',
                  lineHeight: 'var(--leading-body)',
                }}
              >
                {emptyLabel ?? <>Nothing matches “{query}”.</>}
              </Command.Empty>
              {grouped(options).map((run, index) => {
                const items = run.options.map((option) => {
                  const isSelected = option.value === value;
                  return (
                    <Command.Item
                      key={option.value}
                      className="ui-option"
                      value={option.value}
                      keywords={[
                        option.label,
                        option.detail,
                        option.meta,
                        option.description,
                      ].filter((k): k is string => k !== undefined)}
                      disabled={option.disabled ?? false}
                      onSelect={() => pick(option.value)}
                      style={{
                        display: 'flex',
                        alignItems: option.meta || option.description ? 'flex-start' : 'center',
                        gap: 9,
                        padding: '7px 7px',
                        borderRadius: 'var(--radius-tile)',
                        cursor: option.disabled ? 'not-allowed' : 'pointer',
                        opacity: option.disabled ? 0.42 : 1,
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display: 'block',
                            fontSize: 'var(--text-body-sm)',
                            fontWeight: isSelected
                              ? 'var(--weight-semibold)'
                              : 'var(--weight-regular)',
                            color: 'var(--text-strong)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {option.label}
                          {option.detail ? (
                            <span
                              className="mono"
                              style={{
                                marginLeft: 7,
                                fontSize: 'var(--text-label-lg)',
                                fontWeight: 'var(--weight-regular)',
                                color: 'var(--text-faint)',
                              }}
                            >
                              {option.detail}
                            </span>
                          ) : null}
                        </span>
                        {option.description ? (
                          <span
                            style={{
                              display: 'block',
                              fontSize: 'var(--text-meta)',
                              color: 'var(--text-muted)',
                              marginTop: 2,
                            }}
                          >
                            {option.description}
                          </span>
                        ) : null}
                        {option.meta ? (
                          <span
                            className="mono"
                            style={{
                              display: 'block',
                              fontSize: 'var(--text-label-lg)',
                              color: 'var(--text-faint)',
                              marginTop: 2,
                            }}
                          >
                            {option.meta}
                          </span>
                        ) : null}
                      </span>
                      {option.aside ? (
                        <span
                          className="mono"
                          style={{
                            flexShrink: 0,
                            fontSize: 'var(--text-label-lg)',
                            color: option.asideTone === 'warn' ? 'var(--warn)' : 'var(--text-soft)',
                          }}
                        >
                          {option.aside}
                        </span>
                      ) : null}
                      {isSelected ? (
                        <Icon
                          name="check"
                          size={16}
                          color="var(--accent)"
                          style={{ flexShrink: 0 }}
                        />
                      ) : null}
                    </Command.Item>
                  );
                });
                return run.group ? (
                  <Command.Group key={`${index}:${run.group}`} heading={run.group}>
                    {items}
                  </Command.Group>
                ) : (
                  <Fragment key={index}>{items}</Fragment>
                );
              })}
            </Command.List>
            {offerCreate ? (
              <Command.Item
                className="ui-option"
                value={`create ${trimmed}`}
                forceMount
                onSelect={() => {
                  onCreate(trimmed);
                  close();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  padding: '9px 11px',
                  borderTop: '1px solid var(--border-divider)',
                  cursor: 'pointer',
                  fontSize: 'var(--text-body-sm)',
                  fontWeight: 'var(--weight-medium)',
                  color: 'var(--text-accent)',
                }}
              >
                <Icon name="add" size={16} />
                {createLabel ? createLabel(trimmed) : <>Create “{trimmed}”</>}
              </Command.Item>
            ) : null}
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
